import { authApi, accountApi, apiClient } from '../../services/api';
// ===== AUTH SERVICES =====
export const authService = {
    // Login
    login: async (credentials) => {
        try {
            const response = await authApi.login(credentials.email, credentials.password);
            // Validate response structure
            if (!response.user || !response.access_token) {
                throw new Error('Invalid login response structure');
            }
            return {
                user: response.user,
                tokens: {
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                },
                enrichedData: response.enrichedData,
            };
        }
        catch (error) {
            console.error('Login service error:', error);
            throw error;
        }
    },
    // Register
    register: async (userData) => {
        try {
            const response = await authApi.register(userData);
            // Validate response structure
            if (!response.user || !response.access_token) {
                throw new Error('Invalid registration response structure');
            }
            return {
                user: response.user,
                tokens: {
                    access_token: response.access_token,
                    refresh_token: response.refresh_token,
                },
            };
        }
        catch (error) {
            console.error('Registration service error:', error);
            throw error;
        }
    },
    // Refresh token
    refreshToken: async (refreshToken) => {
        try {
            const response = await authApi.refreshToken(refreshToken);
            // Validate response structure
            if (!response.access_token) {
                throw new Error('Invalid refresh token response structure');
            }
            return {
                access_token: response.access_token,
                refresh_token: response.refresh_token,
            };
        }
        catch (error) {
            console.error('Refresh token service error:', error);
            throw error;
        }
    },
    // Logout
    logout: async () => {
        try {
            console.log('🚪 AuthService: Attempting API logout...');
            await authApi.logout();
            console.log('✅ AuthService: API logout successful');
        }
        catch (error) {
            console.warn('⚠️ AuthService: API logout failed, but continuing with local logout:', error);
            // Don't throw error for logout - always succeed locally
            // This ensures the user is logged out even if the API is down
        }
    },
    // Logout all
    logoutAll: async () => {
        try {
            await authApi.logoutAll();
        }
        catch (error) {
            console.error('Logout all service error:', error);
            // Don't throw error for logout - always succeed locally
        }
    },
    // Get profile
    getProfile: async () => {
        try {
            const response = await authApi.getProfile();
            return response.user || response;
        }
        catch (error) {
            console.error('Get profile service error:', error);
            throw error;
        }
    },
    // Update profile
    updateProfile: async (profileData) => {
        try {
            const response = await authApi.updateProfile(profileData);
            return response.user || response;
        }
        catch (error) {
            console.error('Update profile service error:', error);
            throw error;
        }
    },
    // Change password
    changePassword: async (passwordData) => {
        try {
            const response = await authApi.changePassword(passwordData);
            return response;
        }
        catch (error) {
            console.error('Change password service error:', error);
            throw error;
        }
    },
    // Get active sessions
    getActiveSessions: async () => {
        try {
            const response = await authApi.getActiveSessions();
            return response.sessions || response;
        }
        catch (error) {
            console.error('Get active sessions service error:', error);
            throw error;
        }
    },
    // Health check
    healthCheck: async () => {
        try {
            return await apiClient.healthCheck();
        }
        catch (error) {
            console.error('Health check service error:', error);
            return false;
        }
    },
};
// ===== ACCOUNT SERVICES =====
export const accountService = {
    // Create account
    createAccount: async (accountData) => {
        try {
            const response = await accountApi.createAccount(accountData);
            // Validate response structure
            if (!response.account) {
                throw new Error('Invalid create account response structure');
            }
            return {
                account: response.account,
                owner: response.owner,
                tokens: response.tokens ? {
                    access_token: response.tokens.access_token,
                    refresh_token: response.tokens.refresh_token,
                } : undefined,
            };
        }
        catch (error) {
            console.error('Create account service error:', error);
            throw error;
        }
    },
    // Get accounts
    getAccounts: async () => {
        try {
            const response = await accountApi.getAccounts();
            return response.accounts || response;
        }
        catch (error) {
            console.error('Get accounts service error:', error);
            throw error;
        }
    },
    // Get account by ID
    getAccount: async (id) => {
        try {
            const response = await accountApi.getAccount(id);
            return response.account || response;
        }
        catch (error) {
            console.error('Get account service error:', error);
            throw error;
        }
    },
    // Update account
    updateAccount: async (id, accountData) => {
        try {
            const response = await accountApi.updateAccount(id, accountData);
            return response.account || response;
        }
        catch (error) {
            console.error('Update account service error:', error);
            throw error;
        }
    },
};
// ===== UTILITY SERVICES =====
export const utilityService = {
    // Test API connection
    testConnection: async () => {
        try {
            const startTime = Date.now();
            const isConnected = await apiClient.healthCheck();
            const latency = Date.now() - startTime;
            return {
                isConnected,
                latency: isConnected ? latency : undefined,
            };
        }
        catch (error) {
            console.error('Test connection service error:', error);
            return { isConnected: false };
        }
    },
    // Get API status
    getApiStatus: async () => {
        try {
            const { isConnected, latency } = await utilityService.testConnection();
            return {
                status: isConnected ? 'connected' : 'mock',
                isConnected,
                latency,
                timestamp: new Date().toISOString(),
            };
        }
        catch (error) {
            console.error('Get API status service error:', error);
            return {
                status: 'mock',
                isConnected: false,
                timestamp: new Date().toISOString(),
            };
        }
    },
};
